import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router';

import { AppDispatch } from './store';
import { setTheme, setView } from './store/layoutSlice';
import { logout, readSession, UserState } from './store/userSlice';

import { Environment } from './Environment';
import { InviteLanding } from './InviteLanding';
import { LoginCode } from './containers/Login/LoginCode';
import { LoginPage } from './containers/Login/LoginPage';
import { MagicLink } from './containers/Login/MagicLink';
import Workspace from './containers/Workspace/Workspace';

import Button from 'atoms/Button';
import ScrollToTop from 'atoms/ScrollToTop';
import AutomationPage from 'containers/Automation';
import { AutomationConfigPage } from 'containers/Automation/AutomationConfigPage';
import { AutomationCreatePage } from 'containers/Automation/AutomationCreatePage';
import FeedbackIdeasPage from 'containers/FeedbackIdeas';
import SubscriptionPage from 'containers/Subscription';
import SubscriptionSuccessPage from 'containers/Subscription/SubscriptionSuccessPage';
import Loader from 'molecules/Loader';
import Layout from 'organisms/Layout';
import Topbar from 'organisms/Topbar';

const App = () => {
    const dispatch = useDispatch<AppDispatch>();
    const [loading, setLoading] = useState(true);
    const [appError, setAppError] = useState<any>(null);

    const userState: UserState = useSelector((state: any) => state.user);

    const checkUserLoggedIn = async () => {
        if (userState.data) {
            return;
        }
        const storedToken = localStorage.getItem('hustleToken');
        if (!storedToken) {
            const params = new URLSearchParams(window.location.search);
            const token = params.get('token');
            if (token) {
                localStorage.setItem('hustleToken', token);
                params.delete('token');

                const newSearchParams = params.toString();
                const newUrl =
                    window.location.pathname +
                    (newSearchParams ? `?${newSearchParams}` : '');

                window.location.replace(newUrl);
            }
        }
        dispatch(readSession()).finally(() => setLoading(false));
    };

    useEffect(() => {
        checkUserLoggedIn();
    }, []);

    const onUserLoggedIn = async () => {
        await checkUserLoggedIn();
    };

    const getUserInfo = async (session: string) => {
        try {
            const response = await fetch(`${Environment.backendUrl}/session`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${session}`,
                },
            });
            if (response.ok) {
                return response.json();
            } else {
                setAppError('Cannot read user data: ' + response.status);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const signOut = async () => {
        localStorage.removeItem('hustleToken');
        dispatch(logout());
    };

    useEffect(() => {
        const darkThemeEnabled = localStorage.getItem('hustleTheme') === 'dark';
        if (darkThemeEnabled) {
            document.body.classList.add('dark-theme');
            document.documentElement.style.backgroundColor = '#191919';
            document.body.style.backgroundColor = '#191919';
            dispatch(setTheme('dark'));
        } else {
            document.body.classList.remove('dark-theme');
            document.documentElement.style.backgroundColor = '';
            document.body.style.backgroundColor = '';
            dispatch(setTheme('light'));
        }

        const savedView = localStorage.getItem('hustleView');
        if (savedView === 'grid' || savedView === 'list') {
            dispatch(setView(savedView));
        }
    }, []);

    if (appError) {
        return (
            <Layout>
                App error: {appError}
                <Button onClick={signOut}>Logout</Button>
            </Layout>
        );
    }

    if (loading) {
        return (
            <Layout>
                <Loader initLoader withLogo />
            </Layout>
        );
    }

    return (
        <>
            <ScrollToTop />
            <Topbar active={userState.data !== null} />
            <Layout>
                {userState.data === null && (
                    <Routes>
                        <Route
                            path="/magic-link/sent"
                            element={<MagicLink mode={'EMAIL_SENT'} />}
                        />
                        <Route
                            path="/magic-link"
                            element={<MagicLink mode={'GET_EMAIL'} />}
                        />
                        <Route
                            path="/login"
                            element={<LoginCode mode={'GET_EMAIL'} />}
                        />
                        <Route
                            path="/login"
                            element={
                                <LoginPage
                                    onUserLoggedIn={onUserLoggedIn}
                                ></LoginPage>
                            }
                        />
                        <Route
                            path="/magic-link"
                            element={<MagicLink mode={'GET_EMAIL'} />}
                        />
                        <Route
                            path="/*"
                            element={
                                <LoginPage
                                    onUserLoggedIn={onUserLoggedIn}
                                ></LoginPage>
                            }
                        />
                    </Routes>
                )}
                {userState.data !== null && (
                    <Routes>
                        <Route
                            path="/plan"
                            element={<Workspace workspaceState="PLAN" />}
                        />
                        <Route
                            path="/execute"
                            element={<Workspace workspaceState="EXECUTE" />}
                        />
                        <Route
                            path="/automation"
                            element={<AutomationPage />}
                        />
                        <Route
                            path="/automation/configure"
                            element={<AutomationConfigPage />}
                        />
                        <Route
                            path="/automation/configure/create"
                            element={<AutomationCreatePage />}
                        />
                        <Route
                            path="/feedback-ideas"
                            element={<FeedbackIdeasPage />}
                        />
                        <Route
                            path="/subscription"
                            element={<SubscriptionPage />}
                        />
                        <Route
                            path="/subscription/success"
                            element={<SubscriptionSuccessPage />}
                        />
                        <Route
                            path="/invite/:inviteToken"
                            element={<InviteLanding />}
                        />
                        <Route path="*" element={<Navigate to="/execute" />} />
                    </Routes>
                )}
            </Layout>
        </>
    );
};

export default App;
